/**
 * Session Traces → Usage.
 *
 * What the product is actually being used for: the ranked surfaces — pages,
 * in-page screens, marked clicks — for one closed period, and how any one of
 * them is moving over time.
 *
 * Reads only the daily rollup, never session traces or the raw event stream.
 * Scope follows the sidebar tenant filter: a selected tenant is single-partition,
 * "All tenants" sums across them.
 *
 * See docs/FeatureDesigns/user-activity-analytics.md.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  SearchFilterBar,
  DataTable,
  type FilterDef,
  type ColumnDef,
  type SortState,
} from '@shared-ui/components';
import { useSelectedTenantId, hasScopedTenant } from '../../components/TenantFilter';
import { fetchWithAdmin } from '../../api/fetchHelper';
import { SurfaceTrend } from './SurfaceTrend';
import { periodOptions } from './periods';
import type { RollupPeriod, SurfaceTotals, UsageResponse } from './types';
import { formatDuration } from './format';
import './UserActions.css';

export function UsageTab() {
  const selectedTenant = useSelectedTenantId();
  const tenantId = hasScopedTenant(selectedTenant) ? selectedTenant : undefined;

  const [applied, setApplied] = useState<Record<string, string>>(() => ({
    period: 'week',
    periodStart: periodOptions('week', new Date())[0]?.value ?? '',
    kind: 'page_view',
    appSource: '',
  }));
  const [sort, setSort] = useState<SortState>({ sortBy: 'events', sortOrder: 'desc' });

  const period = (applied.period || 'week') as RollupPeriod;
  const { periodStart, kind, appSource } = applied;

  const periods = useMemo(() => periodOptions(period, new Date()), [period]);

  const usage = useQuery({
    queryKey: ['user-activity-usage', tenantId, period, periodStart, kind, appSource],
    enabled: !!periodStart,
    queryFn: () => {
      const p = new URLSearchParams({ period, periodStart, kind });
      if (tenantId) p.set('tenantId', tenantId);
      if (appSource) p.set('appSource', appSource);
      return fetchWithAdmin<UsageResponse>(`/console/user-activity-usage?${p}`);
    },
  });

  const filterDefs: FilterDef[] = [
    {
      key: 'kind',
      kind: 'chips',
      label: 'Show',
      options: [
        { value: 'page_view', label: 'Pages' },
        { value: 'screen_view', label: 'Screens' },
        { value: 'click', label: 'Clicks' },
      ],
    },
    { key: 'period', kind: 'chips', label: 'Grain', options: [
      { value: 'day', label: 'Day' },
      { value: 'week', label: 'Week' },
      { value: 'month', label: 'Month' },
    ] },
    { key: 'periodStart', placeholder: 'Period', required: true, options: periods },
    {
      key: 'appSource',
      placeholder: 'All apps',
      options: [
        { value: 'web-student', label: 'Student web' },
        { value: 'web-teacher', label: 'Teacher web' },
        { value: 'ios', label: 'iOS' },
        { value: 'android', label: 'Android' },
      ],
    },
  ];

  /**
   * Pages are their own label; screens and clicks hang off one.
   *
   * A surface arrives as one string and the KIND decides where it splits — `#`
   * for a screen, the LAST `:` for a control, because a route pattern contains
   * colons of its own (`/question/:id:submit` is the submit control on the
   * question page). Not split on the wire: tier 3 keys on `(kind, target)` and
   * decomposing it server-side would invent a shape the store does not have.
   */
  const surfaceParts = (s: SurfaceTotals): [string, string?] => {
    if (kind === 'screen_view') {
      const i = s.surface.indexOf('#');
      return i < 0 ? [s.surface] : [s.surface.slice(0, i), s.surface.slice(i + 1)];
    }
    if (kind === 'click') {
      const i = s.surface.lastIndexOf(':');
      return i <= 0 ? [s.surface] : [s.surface.slice(0, i), s.surface.slice(i + 1)];
    }
    return [s.surface];
  };

  const surfaceLabel = (s: SurfaceTotals) => {
    const [route, leaf] = surfaceParts(s);
    return (
      <span className="ua-route">
        <span>{route}</span>
        {leaf && <span className="ua-arrow"> › {leaf}</span>}
      </span>
    );
  };

  const columns: ColumnDef<SurfaceTotals>[] = [
    { key: 'surface', header: 'Surface', mobile: 'title', render: surfaceLabel },
    { key: 'events', header: 'Events', sortable: true, render: s => s.events.toLocaleString() },
    {
      key: 'sessions',
      header: 'Sessions',
      sortable: true,
      // Absent where nothing measured a sitting — an em dash, not a zero.
      render: s => (s.sessions === undefined ? '—' : s.sessions.toLocaleString()),
    },
    {
      key: 'users',
      header: 'Users',
      sortable: true,
      render: s => s.users.toLocaleString(),
    },
    {
      key: 'dwellMs',
      header: 'Time',
      sortable: true,
      className: 'ua-dim',
      // Clicks carry no dwell — an em dash, not a zero.
      render: s => (s.dwellMs ? formatDuration(s.dwellMs) : '—'),
    },
  ];

  // Sorting is client-side: a period's rows are already aggregated down to a
  // bounded top-N, so there is nothing to gain from a round trip.
  const rows = useMemo(() => {
    const data = [...(usage.data?.surfaces ?? [])];
    const key = sort.sortBy as keyof SurfaceTotals;
    const dir = sort.sortOrder === 'asc' ? 1 : -1;
    return data.sort((a, b) => ((Number(a[key] ?? 0) - Number(b[key] ?? 0)) * dir));
  }, [usage.data, sort]);

  const scopeNote = tenantId ? `tenant ${tenantId}` : 'all tenants';

  return (
    <div className="ua-list">
      <SearchFilterBar
        filters={filterDefs}
        values={applied}
        hideSearch
        onChange={next => {
          // Switching grain invalidates the chosen period — re-seed it to the
          // newest closed one rather than querying for a key that can't exist.
          const grainChanged = next.period !== applied.period;
          setApplied(grainChanged
            ? { ...next, periodStart: periodOptions(next.period as RollupPeriod, new Date())[0]?.value ?? '' }
            : next);
        }}
      />

      {/* A failed query renders as the error alone. Showing an empty table
          beside it would read as "nothing was used", which is a different and
          much more misleading claim than "we couldn't tell". */}
      {usage.isError ? (
        <p className="ua-error">Failed: {(usage.error as Error).message}</p>
      ) : usage.data && usage.data.surfaces.length === 0 && !usage.isFetching ? (
        <p className="ua-empty">
          No usage recorded for this period ({scopeNote}). Rollups are written once a period
          closes, so the current day, week or month won't appear until it does — and only
          sessions from users with the <code>user_session_trace</code> gate enabled are counted.
        </p>
      ) : (
        <>
          {/* Only once there is something to count — "0 surfaces" while the
              query is still in flight states a result we don't have yet. */}
          <div className="ua-count">
            {usage.data ? `${rows.length.toLocaleString()} surfaces · ${scopeNote}` : ''}
          </div>

          <DataTable
            className="ua-table"
            columns={columns}
            data={rows}
            rowKey={s => s.surface}
            loading={usage.isLoading}
            sort={sort}
            onSortChange={setSort}
            renderExpandedRow={s => (
              <SurfaceTrend
                period={period}
                kind={kind}
                surface={s.surface}
                tenantId={tenantId}
              />
            )}
          />
        </>
      )}
    </div>
  );
}
